import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, Popover } from '@mui/material';
import { Chess } from 'chess.js';

// ── Piece style sets ──
export const svgPieceUrl = (set, key) => `/piece/${set}/${key}.svg`;

export const PIECE_SETS = {
  cburnett: { label: 'Cburnett', type: 'svg', preview: { w: '/piece/cburnett/wN.svg', b: '/piece/cburnett/bN.svg' } },
};

export const BOARD_THEMES = {
  amethyst: { label: 'Amethyst', light: '#6e6590', dark: '#413964', arrow: '#8F7BE0' },
  frost:    { label: 'Frost',    light: '#57A5BA', dark: '#33516A', arrow: '#00A5C4' },
  obsidian: { label: 'Obsidian', light: '#585862', dark: '#2c2c35', arrow: '#9BA3B0' },
  ivory:    { label: 'Ivory',    light: '#e8dfd0', dark: '#a89478', arrow: '#C4973B' },
  slate:    { label: 'Slate',    light: '#b0b8c4', dark: '#6a7484', arrow: '#6B8AAF' },
};

const getPieceKey = (piece) => piece ? piece.color + piece.type : null;

// ── Move sounds ──
const audioCtx = typeof window !== 'undefined' ? new (window.AudioContext || window.webkitAudioContext)() : null;

export function playSound(type = 'move') {
  if (!audioCtx) return;
  if (audioCtx.state === 'suspended') audioCtx.resume();
  const t = audioCtx.currentTime;
  const g = audioCtx.createGain();
  g.connect(audioCtx.destination);

  if (type === 'capture') {
    // Sharp percussive thud
    const osc = audioCtx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(180, t);
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.08);
    g.gain.setValueAtTime(0.35, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc.connect(g); osc.start(t); osc.stop(t + 0.18);
    // Add noise burst
    const noise = audioCtx.createOscillator();
    noise.type = 'sawtooth';
    noise.frequency.setValueAtTime(800, t);
    noise.frequency.exponentialRampToValueAtTime(200, t + 0.05);
    const ng = audioCtx.createGain();
    ng.gain.setValueAtTime(0.12, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    noise.connect(ng); ng.connect(audioCtx.destination);
    noise.start(t); noise.stop(t + 0.1);
  } else if (type === 'check') {
    // High alert tone
    const osc = audioCtx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(660, t);
    osc.frequency.setValueAtTime(880, t + 0.08);
    g.gain.setValueAtTime(0.2, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
    osc.connect(g); osc.start(t); osc.stop(t + 0.28);
  } else if (type === 'castle') {
    // Two-tap sound
    const osc1 = audioCtx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(220, t);
    osc1.frequency.exponentialRampToValueAtTime(120, t + 0.08);
    const g1 = audioCtx.createGain();
    g1.gain.setValueAtTime(0.2, t);
    g1.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
    osc1.connect(g1); g1.connect(audioCtx.destination);
    osc1.start(t); osc1.stop(t + 0.12);
    const osc2 = audioCtx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(260, t + 0.12);
    osc2.frequency.exponentialRampToValueAtTime(140, t + 0.2);
    const g2 = audioCtx.createGain();
    g2.gain.setValueAtTime(0.2, t + 0.12);
    g2.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
    osc2.connect(g2); g2.connect(audioCtx.destination);
    osc2.start(t + 0.12); osc2.stop(t + 0.25);
  } else if (type === 'gameOver') {
    // Descending chord
    [440, 349, 293].forEach((freq, i) => {
      const osc = audioCtx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t + i * 0.15);
      const og = audioCtx.createGain();
      og.gain.setValueAtTime(0.15, t + i * 0.15);
      og.gain.exponentialRampToValueAtTime(0.001, t + i * 0.15 + 0.4);
      osc.connect(og); og.connect(audioCtx.destination);
      osc.start(t + i * 0.15); osc.stop(t + i * 0.15 + 0.45);
    });
  } else {
    // Normal move — soft tap
    const osc = audioCtx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(240, t);
    osc.frequency.exponentialRampToValueAtTime(110, t + 0.1);
    g.gain.setValueAtTime(0.2, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    osc.connect(g); osc.start(t); osc.stop(t + 0.15);
  }
}

export function getSoundType(move) {
  if (move.san.includes('#')) return 'gameOver';
  if (move.san.includes('+')) return 'check';
  if (move.san === 'O-O' || move.san === 'O-O-O') return 'castle';
  if (move.captured) return 'capture';
  return 'move';
}

// ── InteractiveBoard Component ──
export default function InteractiveBoard({
  // Required
  c,

  // Board state (managed by parent)
  fen,
  flipped,
  pieceSet,
  boardTheme,
  selectedSquare,
  legalMoves,
  lastMove,

  // Callbacks (managed by parent)
  onSquareClick,
  onDragStart,
  onFlip,
  onGoTo,

  // State setters needed for internal interactions
  setArrows,
  setHighlightedSquares,
  setPieceSet,
  setBoardTheme,

  // Internal state passed through
  arrows,
  highlightedSquares,
  dragFrom,
  dragPos,
  dragPiece,
  hoveredSquare,
  setHoveredSquare,
  pendingPromotion,
  onPromotionChoice,
  setPendingPromotion,

  // Navigation
  positions,
  moveIndex,

  // Optional overlays
  heatmapData,
  threatData,

  // Optional: eval bar beside board
  showEvalBar = false,
  evalPercent,
  materialAdvantage,
}) {
  // ── Internal state ──
  const [coordsMode, setCoordsMode] = useState('inside');
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [arrowStart, setArrowStart] = useState(null);
  const boardRef = useRef(null);

  // ── Derived board data ──
  const boardChess = useMemo(() => {
    const bc = new Chess();
    if (fen) bc.load(fen);
    return bc;
  }, [fen]);

  const board = boardChess.board();
  const rows = flipped ? [...board].reverse().map(r => [...r].reverse()) : board;
  const files = flipped ? 'hgfedcba'.split('') : 'abcdefgh'.split('');
  const ranks = flipped ? '12345678'.split('') : '87654321'.split('');

  const squareName = useCallback((ri, ci) => {
    const f = flipped ? 7 - ci : ci;
    const r = flipped ? ri : 7 - ri;
    return 'abcdefgh'[f] + (r + 1);
  }, [flipped]);

  // ── Premove hover — show legal moves when hovering over a piece ──
  const hoverMoves = useMemo(() => {
    if (!hoveredSquare || selectedSquare || dragFrom) return [];
    const piece = boardChess.get(hoveredSquare);
    if (!piece || piece.color !== boardChess.turn()) return [];
    return boardChess.moves({ square: hoveredSquare, verbose: true }).map(m => m.to);
  }, [hoveredSquare, selectedSquare, dragFrom, fen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowLeft') { if (onGoTo) onGoTo(moveIndex - 1); }
      else if (e.key === 'ArrowRight') { if (onGoTo) onGoTo(moveIndex + 1); }
      else if (e.key === 'Home') { if (onGoTo) onGoTo(0); }
      else if (e.key === 'End') { if (onGoTo && positions) onGoTo(positions.length - 1); }
      else if (e.key === 'f' || e.key === 'F') { if (onFlip) onFlip(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onGoTo, onFlip, moveIndex, positions]);

  const arrowColor = (BOARD_THEMES[boardTheme] || BOARD_THEMES.amethyst).arrow;

  const btnSx = {
    px: 2.5, py: 1.5, borderRadius: '12px', cursor: 'pointer', fontSize: '1rem', fontWeight: 600,
    color: c.textMuted, bgcolor: `${c.textMuted}08`, border: `1px solid ${c.textMuted}12`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s ease',
    '&:hover': { bgcolor: `${c.accent}10`, color: c.accent, borderColor: `${c.accent}25` },
  };

  return (
    <Box sx={{ flexShrink: { xs: 1, lg: 0 }, minWidth: 0, position: 'relative' }}>

      {/* ── Settings Popover ── */}
      <Popover open={Boolean(settingsAnchor)} anchorEl={settingsAnchor} onClose={() => setSettingsAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { mt: 1, borderRadius: '16px', bgcolor: c.mode === 'dark' ? '#252530' : '#fff', border: `1px solid ${c.textMuted}15`, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', p: 2.5, minWidth: 260, maxHeight: '70vh', overflowY: 'auto' } } }}>

        {/* Board Theme */}
        <Typography sx={{ color: c.textMuted, fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1 }}>Board Theme</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
          {Object.entries(BOARD_THEMES).map(([key, theme]) => (
            <Box key={key} onClick={() => setBoardTheme(key)} sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: '10px', cursor: 'pointer',
              bgcolor: boardTheme === key ? `${c.accent}12` : 'transparent',
              border: `1.5px solid ${boardTheme === key ? c.accent : 'transparent'}`,
              transition: 'all 0.15s ease', '&:hover': { bgcolor: boardTheme === key ? `${c.accent}12` : `${c.textMuted}08` },
            }}>
              <Box sx={{ display: 'flex', borderRadius: '4px', overflow: 'hidden', flexShrink: 0 }}>
                <Box sx={{ width: 14, height: 14, bgcolor: theme.light }} />
                <Box sx={{ width: 14, height: 14, bgcolor: theme.dark }} />
                <Box sx={{ width: 14, height: 14, bgcolor: theme.dark }} />
                <Box sx={{ width: 14, height: 14, bgcolor: theme.light }} />
              </Box>
              <Typography sx={{ color: boardTheme === key ? c.accent : c.textDark, fontSize: '0.82rem', fontWeight: boardTheme === key ? 700 : 500 }}>{theme.label}</Typography>
            </Box>
          ))}
        </Box>

        {/* Coordinates */}
        <Typography sx={{ color: c.textMuted, fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1, mt: 2.5 }}>Coordinates</Typography>
        <Box sx={{ display: 'flex', gap: 0.6 }}>
          {[
            { key: 'outside', label: 'Outside' },
            { key: 'inside', label: 'Inside' },
            { key: 'hidden', label: 'Hidden' },
          ].map((opt) => (
            <Box key={opt.key} onClick={() => setCoordsMode(opt.key)} sx={{
              flex: 1, py: 0.8, borderRadius: '8px', cursor: 'pointer', textAlign: 'center',
              bgcolor: coordsMode === opt.key ? `${c.accent}15` : `${c.textMuted}06`,
              border: `1.5px solid ${coordsMode === opt.key ? c.accent : 'transparent'}`,
              transition: 'all 0.15s ease',
              '&:hover': { bgcolor: coordsMode === opt.key ? `${c.accent}15` : `${c.textMuted}10` },
            }}>
              <Typography sx={{ color: coordsMode === opt.key ? c.accent : c.textMuted, fontSize: '0.7rem', fontWeight: 600 }}>{opt.label}</Typography>
            </Box>
          ))}
        </Box>

        {/* Keyboard Shortcuts */}
        <Typography sx={{ color: c.textMuted, fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1, mt: 2.5 }}>Keyboard Shortcuts</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
          {[{ key: '← →', action: 'Prev / Next move' }, { key: 'F', action: 'Flip board' }].map((s) => (
            <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: c.accent, fontSize: '0.72rem', fontWeight: 700, bgcolor: `${c.accent}08`, px: 0.8, py: 0.2, borderRadius: '5px', border: `1px solid ${c.accent}15`, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{s.key}</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: '0.72rem' }}>{s.action}</Typography>
            </Box>
          ))}
        </Box>
      </Popover>

      {/* ── Board with optional outside coordinates ── */}
      <Box sx={{ display: 'inline-flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>

          {/* Left rank labels (outside) */}
          {coordsMode === 'outside' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', width: 28, flexShrink: 0, mr: 0.8 }}>
              {ranks.map((r) => (
                <Box key={r} sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: `${c.textMuted}35`, userSelect: 'none', lineHeight: 1 }}>{r}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* ── 8x8 Board Grid ── */}
          <Box ref={boardRef} data-board-grid sx={{
            display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', position: 'relative',
            width: { xs: 'min(96vw, 420px)', sm: 'min(85vw, 480px)', md: 'min(calc(100vh - 280px), calc(100vw - 900px))', lg: 'min(calc(100vh - 280px), calc(100vw - 1000px))' },
            aspectRatio: '1 / 1', borderRadius: '10px', overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            {rows.map((row, ri) =>
              row.map((piece, ci) => {
                const sq = squareName(ri, ci);
                const isDragging = dragFrom === sq;
                const isLight = (ri + ci) % 2 === 0;
                const isSelected = sq === selectedSquare;
                const isLegal = legalMoves.includes(sq);
                const isHoverTarget = hoverMoves.includes(sq);
                const isLastFrom = lastMove?.from === sq;
                const isLastTo = lastMove?.to === sq;
                const pk = getPieceKey(piece);
                const bt = BOARD_THEMES[boardTheme] || BOARD_THEMES.amethyst;
                const curSet = PIECE_SETS[pieceSet] || PIECE_SETS.cburnett;
                const isSvg = curSet.type === 'svg';
                const baseBg = isLight ? bt.light : bt.dark;
                let bgColor = baseBg;
                let overlayColor = null;
                if (isSelected) bgColor = `${c.accent}50`;
                else if (isLastFrom || isLastTo) overlayColor = 'rgba(68, 196, 219, 0.2)';
                return (
                  <Box key={sq}
                    onClick={() => { if (!dragFrom) { onSquareClick(ri, ci); setArrows([]); setHighlightedSquares(new Set()); } }}
                    onTouchStart={(e) => onDragStart(ri, ci, e)}
                    onMouseEnter={() => setHoveredSquare(sq)}
                    onMouseLeave={() => setHoveredSquare(null)}
                    onContextMenu={(e) => e.preventDefault()}
                    onMouseDown={(e) => {
                      if (e.button === 2) { e.preventDefault(); setArrowStart(sq); }
                      else if (e.button === 0) { onDragStart(ri, ci, e); }
                    }}
                    onMouseUp={(e) => {
                      if (e.button === 2 && arrowStart && arrowStart !== sq) {
                        setArrows(prev => {
                          const exists = prev.find(a => a.from === arrowStart && a.to === sq);
                          if (exists) return prev.filter(a => !(a.from === arrowStart && a.to === sq));
                          return [...prev, { from: arrowStart, to: sq }];
                        });
                        setArrowStart(null);
                      } else if (e.button === 2 && arrowStart === sq) {
                        // Right-click same square = toggle highlight
                        setHighlightedSquares(prev => {
                          const next = new Set(prev);
                          if (next.has(sq)) next.delete(sq); else next.add(sq);
                          return next;
                        });
                        setArrowStart(null);
                      }
                    }}
                    sx={{
                      position: 'relative', bgcolor: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      aspectRatio: '1 / 1', cursor: dragFrom ? 'grabbing' : 'pointer', transition: 'background-color 0.12s',
                      boxShadow: isDragging ? `inset 0 0 0 3px ${c.accent}90` : 'none',
                      ...(overlayColor ? { '&::after': { content: '""', position: 'absolute', inset: 0, bgcolor: overlayColor, pointerEvents: 'none', zIndex: 1 } } : {}),
                    }}>

                    {/* Inside coordinate labels */}
                    {coordsMode === 'inside' && ci === 0 && <Typography sx={{ position: 'absolute', top: 3, left: 5, fontSize: '1.4rem', fontWeight: 800, color: isLight ? bt.dark : bt.light, opacity: 0.8, userSelect: 'none', zIndex: 2 }}>{ranks[ri]}</Typography>}
                    {coordsMode === 'inside' && ri === 7 && <Typography sx={{ position: 'absolute', bottom: 2, right: 5, fontSize: '1.4rem', fontWeight: 800, color: isLight ? bt.dark : bt.light, opacity: 0.8, userSelect: 'none', zIndex: 2 }}>{files[ci]}</Typography>}

                    {/* Right-click highlight */}
                    {highlightedSquares.has(sq) && <Box sx={{ position: 'absolute', inset: 0, bgcolor: `${arrowColor}35`, zIndex: 1, pointerEvents: 'none' }} />}

                    {/* Heatmap overlay */}
                    {heatmapData && heatmapData[sq] > 0 && <Box sx={{ position: 'absolute', inset: 0, bgcolor: `rgba(143, 123, 224, ${Math.min(heatmapData[sq] * 0.15, 0.55)})`, zIndex: 1, pointerEvents: 'none' }} />}

                    {/* Threat overlay */}
                    {threatData && threatData[sq] && <Box sx={{ position: 'absolute', inset: 0, boxShadow: threatData[sq].defended ? `inset 0 0 0 2px rgba(222, 115, 69, 0.5)` : `inset 0 0 0 3px rgba(227, 82, 120, 0.7)`, zIndex: 1, pointerEvents: 'none' }} />}

                    {/* Legal move dots (empty square) */}
                    {(isLegal || isHoverTarget) && !pk && <Box sx={{ width: '26%', height: '26%', borderRadius: '50%', bgcolor: `${arrowColor}40`, zIndex: 2 }} />}

                    {/* Legal move capture ring (occupied square) */}
                    {(isLegal || isHoverTarget) && pk && <Box sx={{ position: 'absolute', inset: '6%', borderRadius: '50%', border: `3px solid ${arrowColor}50`, zIndex: 2 }} />}

                    {/* SVG piece */}
                    {pk && isSvg && <Box component="img" src={svgPieceUrl(pieceSet, piece.color === 'w' ? `w${piece.type.toUpperCase()}` : `b${piece.type.toUpperCase()}`)} alt="" draggable={false} sx={{ width: '98%', height: '98%', userSelect: 'none', pointerEvents: 'none', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))', opacity: isDragging ? 0.25 : 1, zIndex: 2, position: 'relative' }} />}

                    {/* Unicode piece */}
                    {pk && !isSvg && <Typography sx={{ fontSize: pieceSet === 'letters' ? { xs: 'min(7vw, 2rem)', sm: 'min(6vw, 2.5rem)', md: '3rem' } : { xs: 'min(10vw, 3rem)', sm: 'min(9vw, 3.5rem)', md: '4.4rem' }, lineHeight: 1, userSelect: 'none', fontWeight: pieceSet === 'letters' ? 800 : 400, color: piece.color === 'w' ? '#ECE2DB' : '#111115', textShadow: piece.color === 'w' ? '0 1px 3px rgba(0,0,0,0.4), 0 0 1px rgba(0,0,0,0.2)' : '0 1px 2px rgba(0,0,0,0.5), 0 0 8px rgba(255,255,255,0.15)', opacity: isDragging ? 0.25 : 1, zIndex: 2, position: 'relative' }}>{curSet.pieces[pk]}</Typography>}
                  </Box>
                );
              })
            )}

            {/* ── Arrow SVG overlay ── */}
            {arrows.length > 0 && (
              <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10 }} viewBox="0 0 800 800">
                {arrows.map((arrow, i) => {
                  const color = arrow.color || arrowColor;
                  const fromFile = arrow.from.charCodeAt(0) - 97;
                  const fromRank = parseInt(arrow.from[1]) - 1;
                  const toFile = arrow.to.charCodeAt(0) - 97;
                  const toRank = parseInt(arrow.to[1]) - 1;
                  const cx1 = (flipped ? 7 - fromFile : fromFile) * 100 + 50;
                  const cy1 = (flipped ? fromRank : 7 - fromRank) * 100 + 50;
                  const cx2 = (flipped ? 7 - toFile : toFile) * 100 + 50;
                  const cy2 = (flipped ? toRank : 7 - toRank) * 100 + 50;
                  const fileDiff = Math.abs(toFile - fromFile);
                  const rankDiff = Math.abs(toRank - fromRank);
                  const isKnight = (fileDiff === 1 && rankDiff === 2) || (fileDiff === 2 && rankDiff === 1);
                  const sw = 8; // shaft half-width
                  const hw = 20; // arrowhead half-width
                  const hl = 30; // arrowhead length

                  if (isKnight) {
                    // L-shaped arrow: go along the longer axis first, then turn
                    const longVertical = rankDiff > fileDiff;
                    // Corner point: same file/rank split
                    const cornerX = longVertical ? cx1 : cx2;
                    const cornerY = longVertical ? cy2 : cy1;
                    // Start offset towards corner
                    const d1x = cornerX - cx1;
                    const d1y = cornerY - cy1;
                    const l1 = Math.sqrt(d1x * d1x + d1y * d1y);
                    const sx = cx1 + (d1x / l1) * 30;
                    const sy = cy1 + (d1y / l1) * 30;
                    // Second leg direction
                    const d2x = cx2 - cornerX;
                    const d2y = cy2 - cornerY;
                    const l2 = Math.sqrt(d2x * d2x + d2y * d2y);
                    const n2x = d2x / l2;
                    const n2y = d2y / l2;
                    // Arrowhead base
                    const bx = cx2 - n2x * hl;
                    const by = cy2 - n2y * hl;
                    const p2x = -n2y;
                    const p2y = n2x;
                    // First leg perpendicular
                    const n1x = d1x / l1;
                    const n1y = d1y / l1;
                    const p1x = -n1y;
                    const p1y = n1x;
                    // Outer corner: extend both offset edges to meet at a right angle
                    const outerCornerX = cornerX + p1x * sw + p2x * sw;
                    const outerCornerY = cornerY + p1y * sw + p2y * sw;
                    // Inner corner: the tight side
                    const innerCornerX = cornerX - p1x * sw - p2x * sw;
                    const innerCornerY = cornerY - p1y * sw - p2y * sw;
                    // Build path as a single filled shape
                    const d = [
                      `M ${sx + p1x * sw} ${sy + p1y * sw}`,
                      `L ${outerCornerX} ${outerCornerY}`,
                      `L ${bx + p2x * sw} ${by + p2y * sw}`,
                      `L ${bx + p2x * hw} ${by + p2y * hw}`,
                      `L ${cx2} ${cy2}`,
                      `L ${bx - p2x * hw} ${by - p2y * hw}`,
                      `L ${bx - p2x * sw} ${by - p2y * sw}`,
                      `L ${innerCornerX} ${innerCornerY}`,
                      `L ${sx - p1x * sw} ${sy - p1y * sw}`,
                      'Z'
                    ].join(' ');
                    return <path key={i} d={d} fill={`${color}bb`} opacity="0.7" />;
                  }

                  // Straight arrow
                  const dx0 = cx2 - cx1;
                  const dy0 = cy2 - cy1;
                  const len0 = Math.sqrt(dx0 * dx0 + dy0 * dy0);
                  const x1 = cx1 + (dx0 / len0) * 35;
                  const y1 = cy1 + (dy0 / len0) * 35;
                  const dx = cx2 - x1;
                  const dy = cy2 - y1;
                  const len = Math.sqrt(dx * dx + dy * dy);
                  const nx = dx / len;
                  const ny = dy / len;
                  const px = -ny;
                  const py = nx;
                  const bx = cx2 - nx * hl;
                  const by = cy2 - ny * hl;
                  const points = [
                    `${x1 + px * sw},${y1 + py * sw}`,
                    `${bx + px * sw},${by + py * sw}`,
                    `${bx + px * hw},${by + py * hw}`,
                    `${cx2},${cy2}`,
                    `${bx - px * hw},${by - py * hw}`,
                    `${bx - px * sw},${by - py * sw}`,
                    `${x1 - px * sw},${y1 - py * sw}`,
                  ].join(' ');
                  return <polygon key={i} points={points} fill={`${color}bb`} opacity="0.7" />;
                })}
              </svg>
            )}

            {/* ── Promotion dialog ── */}
            {pendingPromotion && (
              <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.4)' }}
                onClick={() => setPendingPromotion(null)}>
                <Box onClick={(e) => e.stopPropagation()} sx={{
                  display: 'flex', gap: 0.5, p: 1, borderRadius: '14px',
                  bgcolor: c.mode === 'dark' ? '#252530' : '#fff',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                  border: `1px solid ${c.textMuted}20`,
                }}>
                  {['q', 'r', 'b', 'n'].map((type) => (
                    <Box key={type} onClick={() => onPromotionChoice(type)} sx={{
                      width: 56, height: 56, borderRadius: '10px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: `${c.accent}15`, transform: 'scale(1.1)' },
                    }}>
                      <Box component="img"
                        src={svgPieceUrl('cburnett', `${pendingPromotion.color}${type.toUpperCase()}`)}
                        alt={type} sx={{ width: 44, height: 44 }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* ── Optional eval bar ── */}
          {showEvalBar && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', ml: 1.2, width: 28, flexShrink: 0 }}>
              <Typography sx={{ color: `${c.textMuted}80`, fontSize: '1.2rem', fontWeight: 800, mb: 0.3, userSelect: 'none' }}>
                {materialAdvantage > 0 ? `+${materialAdvantage}` : materialAdvantage < 0 ? materialAdvantage : ''}
              </Typography>
              <Box sx={{ flex: 1, width: 18, borderRadius: '6px', bgcolor: '#222226', overflow: 'hidden', border: `1px solid ${c.textMuted}15`, position: 'relative' }}>
                <Box sx={{
                  position: 'absolute', left: 0, right: 0,
                  ...(flipped
                    ? { top: 0, height: `${evalPercent}%`, borderRadius: '6px 6px 0 0' }
                    : { bottom: 0, height: `${evalPercent}%`, borderRadius: '0 0 6px 6px' }),
                  bgcolor: '#ECE2DB19',
                  transition: 'height 0.3s ease',
                }} />
                {/* Center line */}
                <Box sx={{
                  position: 'absolute', top: '50%', left: 0, right: 0, height: 2,
                  transform: 'translateY(-1px)',
                  bgcolor: `${c.accent}19`, zIndex: 1,
                }} />
              </Box>
            </Box>
          )}
        </Box>

        {/* Bottom file labels (outside) */}
        {coordsMode === 'outside' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', ml: '35px', mt: 0.5 }}>
            {files.map((f) => (
              <Box key={f} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.3 }}>
                <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: `${c.textMuted}35`, userSelect: 'none', lineHeight: 1 }}>{f}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* ── Navigation buttons ── */}
      <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center' }}>
        <Box onClick={() => onGoTo(0)} sx={btnSx}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="11 17 6 12 11 7" /><line x1="6" y1="12" x2="7" y2="12" /><line x1="18" y1="5" x2="18" y2="19" /></svg></Box>
        <Box onClick={() => onGoTo(moveIndex - 1)} sx={btnSx}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg></Box>
        <Box onClick={() => onGoTo(moveIndex + 1)} sx={btnSx}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg></Box>
        <Box onClick={() => onGoTo(positions.length - 1)} sx={btnSx}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="13 17 18 12 13 7" /><line x1="18" y1="12" x2="17" y2="12" /><line x1="6" y1="5" x2="6" y2="19" /></svg></Box>
        <Box sx={{ width: 12 }} />
        <Box onClick={() => onFlip()} sx={btnSx} title="Flip board (F)"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="1 4 1 10 7 10" /><polyline points="23 20 23 14 17 14" /><path d="M20.49 9A9 9 0 005.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 013.51 15" /></svg></Box>
        <Box sx={{ width: 12 }} />
        <Box onClick={(e) => setSettingsAnchor(e.currentTarget)} sx={btnSx} title="Board settings">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
          </svg>
        </Box>
      </Box>

      {/* ── Floating drag ghost ── */}
      {dragFrom && dragPos && dragPiece && (() => {
        const curSet = PIECE_SETS[pieceSet] || PIECE_SETS.cburnett;
        const isSvg = curSet.type === 'svg';
        const pk = dragPiece.color + dragPiece.type;
        const sqSize = boardRef.current ? boardRef.current.getBoundingClientRect().width / 8 : 60;
        return (
          <Box sx={{
            position: 'fixed', left: dragPos.x - sqSize / 2, top: dragPos.y - sqSize / 2,
            width: sqSize, height: sqSize,
            pointerEvents: 'none', zIndex: 1000,
            filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))',
          }}>
            {isSvg ? (
              <Box component="img" src={svgPieceUrl(pieceSet, dragPiece.color === 'w' ? `w${dragPiece.type.toUpperCase()}` : `b${dragPiece.type.toUpperCase()}`)} alt="" draggable={false} sx={{ width: '100%', height: '100%' }} />
            ) : (
              <Typography sx={{ fontSize: sqSize * 0.8, lineHeight: 1, textAlign: 'center', userSelect: 'none', fontWeight: pieceSet === 'letters' ? 800 : 400, color: dragPiece.color === 'w' ? '#ECE2DB' : '#111115' }}>{curSet.pieces[pk]}</Typography>
            )}
          </Box>
        );
      })()}
    </Box>
  );
}
